import { useContext, useEffect, useRef } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AuthContext from "../context/auth/AuthContext";
import { haveValue, isValidWorkspaceId } from "../helper/common";
import { useCookies } from "react-cookie";
import LandLordContext from "../context/landlord/LandLordContext";
import TenantContext from "../context/tenant/TenantContext";

const ProtectedRoute = ({ component }) => {
  const authContext = useContext(AuthContext);
  const landLordContext = useContext(LandLordContext);
  const { user, loadUserTenant } = authContext;
  const { linkGurantorAuto } = useContext(TenantContext);
  const { getWorkspaceDetails } = landLordContext;
  const navigate = useNavigate();
  const params = useParams();
  const { workspaceId } = useParams();
  const { assignedPropertyId = null } = params;
  const { pathname } = useLocation();

  const queryParam = new URLSearchParams(window.location.search);
  const [cookies, setCookie, removeCookie] = useCookies(["sharedProfileLink"]);
  const hasAutoGuarantorTriggered = useRef(false);

  useEffect(() => {
    const currentPath = window.location.pathname;
    if (!haveValue(localStorage.getItem("usertype"))) {
      localStorage.setItem("usertype", "tenant");
    }
    if (
      user &&
      haveValue(user?.profile_completed) &&
      user?.profile_completed != true &&
      user?.role == "tenant"
    ) {
      if (
        currentPath !== "/guarantor/onboarding" &&
        currentPath !== "/onboarding"
      ) {
        navigate("/onboarding");
      }
    }

    if (user && user?.role == "landlord") {
      const routesWithUUID = ["/workspace-dashboard", "/help"];

      const isNotWorkspaceRoute = routesWithUUID.includes(pathname);
      if (isNotWorkspaceRoute) return;

      const userWorkspaces = user?.workspaceIds || [];
      const storedWorkspaceId = userWorkspaces?.[0]?.uuid;
      if (!storedWorkspaceId) return;

      // Check if current workspaceId from URL is valid for this user
      const isCurrentWorkspaceValid = userWorkspaces.some(
        (ws) => ws.uuid === workspaceId,
      );

      // If workspaceId is missing or invalid → redirect
      if (
        !workspaceId ||
        (!isValidWorkspaceId(workspaceId) && !isCurrentWorkspaceValid)
      ) {
        let redirectPath = "/home";
        if (pathname.includes("profile-request")) {
          redirectPath = "/profile-request";
        }

        const targetUrl = `/${storedWorkspaceId}${redirectPath}${window.location.search}`;
        if (window.location.pathname + window.location.search !== targetUrl) {
          navigate(targetUrl, {
            replace: true,
          });
          loadUserTenant("landlord");
        }
      }
    }
  }, [user, workspaceId]);

  useEffect(() => {
    const gurantorFor = queryParam.get("gurantorFor");
    const guarantor = queryParam.get("guarantor");
    const accept = queryParam.get("accept");

    if (hasAutoGuarantorTriggered.current) return;

    if (
      haveValue(gurantorFor) &&
      haveValue(guarantor) &&
      haveValue(accept) &&
      accept
    ) {
      hasAutoGuarantorTriggered.current = true;

      window.history.replaceState({}, document.title, window.location.pathname);

      setTimeout(() => {
        linkGurantorAuto(guarantor, gurantorFor)
          .then(() => {})
          .catch((err) => console.log(err));

        navigate("/consent?userId=" + guarantor);
        removeCookie("gurantorFor", { maxAge: 1800 });
      }, 500);
    }
  }, []);

  const redirectToAuth = () => {
    if (haveValue(queryParam.get("requestProfileLink"))) {
      let landlord_id = queryParam.get("requestProfileLink");
      getWorkspaceDetails(landlord_id)
        .then((res) => {
          const data = res.data;
          if (data?.status == "Success") {
            if (
              data?.data?.customLinkEnabled &&
              haveValue(data?.data?.customLinkSlug)
            ) {
              navigate(`/oauth/${data?.data?.customLinkSlug}`);
            } else {
              if (haveValue(data?.data?.invitationLogo)) {
                navigate(`/tenant/auth?wid=${data?.data?.uuid}`);
              } else {
                navigate("/tenant/auth");
              }
            }
            if (data?.data?.catella_rules_activated) {
              setCookie("catellaWorkspaceTenant", `${data?.data?.uuid}`, {
                path: "/",
                maxAge: 3600,
              });
            }
            if (data?.data?.libeen_rules_activated) {
              setCookie("libeenWorkspaceTenant", `${data?.data?.uuid}`, {
                path: "/",
                maxAge: 3600,
              });
            }
          } else {
            navigate("/tenant/auth");
          }
        })
        .catch((err) => {
          console.log(err, "Error in redirectToAuth");
        });
    } else {
      if (haveValue(queryParam.get("gurantorFor"))) {
        navigate("/guarantor/auth");
        return;
      } else if (haveValue(queryParam.get("requestProfileLink"))) {
        navigate("/tenant/auth");
        return;
      }
      navigate("/auth");
    }
  };

  if (window.location.host === process.env.REACT_APP_MACC_URL_NAME) {
    // Retrieve values from localStorage
    const token = localStorage.getItem("token");
    const is_verified = localStorage.getItem("is_verified");
    if (token && is_verified) {
      const usertype = localStorage.getItem("usertype");
      const saveduserid = localStorage.getItem("saveduserid");
      const external_customer_id = localStorage.getItem("external_customer_id");
      const app_type = "macc";
      // Create a query string from the constants
      const queryParams = new URLSearchParams({
        token: token || "", // Append token
        usertype: usertype || "", // Append usertype
        is_verified: is_verified || "", // Append is_verified
        saveduserid: saveduserid || "", // Append saveduserid
        external_customer_id: external_customer_id || "", // Append external_customer_id
        app_type: app_type || "", // // Append app_type
      });
      //clear local storage
      localStorage.removeItem("usertype");
      localStorage.removeItem("is_verified");
      localStorage.removeItem("saveduserid");
      localStorage.removeItem("external_customer_id");
      localStorage.removeItem("macc");
      localStorage.removeItem("loggedin_by");
      // Redirect to the new URL with query parameters
      window.location.replace(
        `${window.location.protocol}//${
          process.env.REACT_APP_URL_NAME
        }/redirect?${queryParams.toString()}`,
      );
      return false;
    }
  }

  if (
    haveValue(queryParam.get("workspace_invite")) &&
    !haveValue(cookies.workspace_invite)
  ) {
    setCookie("workspace_invite", queryParam.get("workspace_invite"), {
      path: "/", // ✅ REQUIRED
      maxAge: 1800,
    });
  }
  if (
    haveValue(queryParam.get("requestProfileLink")) ||
    haveValue(queryParam.get("invitationToken")) ||
    haveValue(queryParam.get("sharedProfileLink"))
  ) {
    if (haveValue(queryParam.get("requestProfileLink"))) {
      setCookie("requestProfileLink", queryParam.get("requestProfileLink"), {
        path: "/",
        maxAge: 604800,
      });
    }

    if (haveValue(queryParam.get("property_id"))) {
      setCookie("property_id", queryParam.get("property_id"), {
        maxAge: 604800,
      });
    }

    if (haveValue(queryParam.get("invitationToken"))) {
      setCookie("invitationToken", queryParam.get("invitationToken"), {
        maxAge: 604800,
      });
      const alreadyLoggedIn = localStorage.getItem("saveduserid");
      if (!alreadyLoggedIn) {
        navigate("/tenant/auth");
      }
    }

    if (haveValue(queryParam.get("sharedProfileLink"))) {
      setCookie("sharedProfileLink", queryParam.get("sharedProfileLink"), {
        path: "/",
        maxAge: 604800,
      });
    }

    // Clean up URL
    const url = new URL(window.location.href);

    if (url.searchParams.has("requestProfileLink")) {
      url.searchParams.delete("requestProfileLink");
    }

    if (url.searchParams.has("property_id")) {
      url.searchParams.delete("property_id");
    }
    if (url.searchParams.has("invitationToken")) {
      url.searchParams.delete("invitationToken");
    }
    if (url.searchParams.has("sharedProfileLink")) {
      url.searchParams.delete("sharedProfileLink");
    }

    window.history.replaceState({}, document.title, url.pathname + url.search);
  }
  if (
    haveValue(queryParam.get("gurantorFor")) &&
    !haveValue(cookies.gurantorFor) &&
    !haveValue(queryParam.get("accept"))
  ) {
    setCookie("gurantorFor", queryParam.get("gurantorFor"), { maxAge: 604800 });
  }
  if (haveValue(queryParam.get("navigateTo"))) {
    setCookie("navigateTo", queryParam.get("navigateTo"), { maxAge: 1800 });
  }

  if (
    haveValue(assignedPropertyId) &&
    window.location.pathname.includes("accept/assigned/property") &&
    haveValue(queryParam.get("email"))
  ) {
    setCookie("assignedPropertyId", assignedPropertyId, { maxAge: 1800 });
  }

  if (haveValue(cookies.navigateTo)) {
    if (haveValue(user?._id)) {
      const navigateTo = cookies.navigateTo;
      removeCookie("navigateTo", { maxAge: 1800 });
      navigate(`/${navigateTo}`);
    }
  }
  if (haveValue(cookies.assignedPropertyId)) {
    const id = cookies.assignedPropertyId;
    removeCookie("assignedPropertyId", { maxAge: 1800 });
    navigate(`/accept/assigned/property/${id}`);
  }

  let token = localStorage.getItem("token");
  let is_verified = localStorage.getItem("is_verified");

  if (!(token && is_verified && is_verified == "true")) {
    const appType = localStorage.getItem("app_type");
    if (haveValue(appType)) {
      localStorage.removeItem("app_type");
      if (appType === "macc") {
        window.location.replace(
          `${window.location.protocol}//${process.env.REACT_APP_MACC_URL_NAME}`,
        );
      } else {
        redirectToAuth();
      }
    } else {
      redirectToAuth();
    }
  }

  return component;
};

export default ProtectedRoute;
